/**
 * Tier-based limits for MentionFox scan functionality
 * WHY: Control feature access based on subscription tier
 */

export interface TierLimits {
  brands: number;
  products: number;
  platforms: string[];
  transcriptionHours: number;
  scansPerMonth: number;
  clipsPerMonth: number;
  maxVideosPerSearch: number;
  widgets: number;
}

export const TIER_LIMITS: Record<string, TierLimits> = {
  free: {
    brands: 1,
    products: 1,
    platforms: ["youtube"],
    transcriptionHours: 0,
    scansPerMonth: 2,
    clipsPerMonth: 0,
    maxVideosPerSearch: 10,
    widgets: 0,
  },
  // The automatic five-day trial gets the Pro allowance.
  trial: {
    brands: 3,
    products: 10,
    platforms: ["youtube", "tiktok"],
    transcriptionHours: 0.5, // 30 min
    scansPerMonth: 10,
    clipsPerMonth: 10,
    maxVideosPerSearch: 100,
    widgets: 3,
  },
  pro: {
    brands: 3,
    products: 10,
    platforms: ["youtube", "tiktok"],
    transcriptionHours: 0.5, // 30 min
    scansPerMonth: 10,
    clipsPerMonth: 10,
    maxVideosPerSearch: 100,
    widgets: 3,
  },
  agency: {
    brands: 20,
    products: 100,
    platforms: ["youtube", "tiktok", "vimeo", "dailymotion", "instagram", "twitter"],
    transcriptionHours: 2, // 120 min
    scansPerMonth: 50,
    clipsPerMonth: 50,
    maxVideosPerSearch: 500,
    widgets: 10,
  },
  enterprise: {
    brands: 999,
    products: 999,
    platforms: ["youtube", "tiktok", "vimeo", "dailymotion", "instagram", "twitter"],
    transcriptionHours: Infinity,
    scansPerMonth: 999,
    clipsPerMonth: 999,
    maxVideosPerSearch: 500,
    widgets: 999,
  },
  custom: {
    brands: 999,
    products: 999,
    platforms: ["youtube", "tiktok", "vimeo", "dailymotion", "instagram", "twitter"],
    transcriptionHours: Infinity,
    scansPerMonth: 999,
    clipsPerMonth: 999,
    maxVideosPerSearch: 500,
    widgets: 999,
  },
};

export function getTierLimits(tier: string): TierLimits {
  return TIER_LIMITS[tier] || TIER_LIMITS.free;
}

export function canUsePlatform(tier: string, platform: string): boolean {
  const limits = getTierLimits(tier);
  return limits.platforms.includes(platform.toLowerCase());
}

export function getMaxScansRemaining(tier: string, scansUsed: number): number {
  const limits = getTierLimits(tier);
  if (limits.scansPerMonth === 999) return Infinity;
  return Math.max(0, limits.scansPerMonth - scansUsed);
}

export const TIER_ORDER: Record<string, number> = {
  free: 0,
  pro: 1,
  agency: 2,
  enterprise: 3,
  custom: 4,
};

export function hasMinimumTier(userTier: string, requiredTier: string): boolean {
  return (TIER_ORDER[userTier] ?? 0) >= (TIER_ORDER[requiredTier] ?? 0);
}
